import { useMemo, useRef, useState, useCallback, useLayoutEffect } from "react";
import dayjs from "dayjs";
import { formatDateTime, formatDate } from "../_shared/utils/dateUtils";

const PALETTE = [
  "#1677ff", "#52c41a", "#fa8c16", "#eb2f96", "#722ed1",
  "#13c2c2", "#faad14", "#2f54eb", "#f5222d", "#a0d911",
];

const BAR_H = 20;
const BAR_GAP = 2;
const LANE_PAD = 4;
const LABEL_W = 220;
const MIN_BAR_W = 4;
const MILESTONE_SIZE = 10;

const PX_PER_DAY: Record<string, number> = {
  day: 100,
  week: 20,
  month: 4,
};

const NOW_PAD_DAYS: Record<string, number> = {
  day: 7,
  week: 30,
  month: 120,
};

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  task: any;
}

interface JobTimelineProps {
  jobs: any[];
  scaleView: "day" | "week" | "month";
  onZoomChange?: (isZoomed: boolean) => void;
  onResetZoom?: () => void;
  zoomRef?: React.MutableRefObject<TimelineHandle | null>;
  /** Changing this re-centers the view on `focusMs` (e.g. when filters change). */
  centerKey?: string;
  /** Where to center when `centerKey` or the scale changes. Defaults to now. */
  focusMs?: number;
  /** Current time for the red "now" line. Defaults to the time of the last layout. */
  nowMs?: number;
}

export interface TimelineHandle {
  reset: () => void;
  scrollToToday?: () => void;
  scrollToDate?: (ms: number) => void;
}

function darken(hex: string, amount = 0.25): string {
  const r = Math.round(parseInt(hex.slice(1, 3), 16) * (1 - amount));
  const g = Math.round(parseInt(hex.slice(3, 5), 16) * (1 - amount));
  const b = Math.round(parseInt(hex.slice(5, 7), 16) * (1 - amount));
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

function lighten(hex: string, amount = 0.35): string {
  const r = Math.round(parseInt(hex.slice(1, 3), 16) + (255 - parseInt(hex.slice(1, 3), 16)) * amount);
  const g = Math.round(parseInt(hex.slice(3, 5), 16) + (255 - parseInt(hex.slice(3, 5), 16)) * amount);
  const b = Math.round(parseInt(hex.slice(5, 7), 16) + (255 - parseInt(hex.slice(5, 7), 16)) * amount);
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

function packIntoSubRows(tasks: any[]): { packed: any[]; subRowCount: number } {
  const subRows: Array<Array<{ start: number; end: number }>> = [];
  const packed = tasks.map((t) => {
    const startMs = t.startMs;
    const endMs = t.isSlotOnly ? startMs + 3600000 : t.endMs;
    for (let r = 0; r < subRows.length; r++) {
      if (!subRows[r].some((s) => s.start < endMs && s.end > startMs)) {
        subRows[r].push({ start: startMs, end: endMs });
        return { ...t, subRow: r };
      }
    }
    subRows.push([{ start: startMs, end: endMs }]);
    return { ...t, subRow: subRows.length - 1 };
  });
  return { packed, subRowCount: Math.max(subRows.length, 1) };
}

function buildDateCells(
  start: dayjs.Dayjs,
  end: dayjs.Dayjs,
  pxPerDay: number,
  scaleView: string
) {
  const topCells: Array<{ label: string; width: number }> = [];
  const bottomCells: Array<{ label: string; width: number; isWeekend: boolean }> = [];

  if (scaleView === "day") {
    let cursor = start.startOf("day");
    let currentMonth = "";
    let monthWidth = 0;
    while (cursor.isBefore(end) || cursor.isSame(end, "day")) {
      const monthLabel = cursor.format("MMMM YYYY");
      if (monthLabel !== currentMonth) {
        if (currentMonth) topCells.push({ label: currentMonth, width: monthWidth });
        currentMonth = monthLabel;
        monthWidth = 0;
      }
      monthWidth += pxPerDay;
      const dow = cursor.day();
      bottomCells.push({
        label: cursor.format("D"),
        width: pxPerDay,
        isWeekend: dow === 0 || dow === 6,
      });
      cursor = cursor.add(1, "day");
    }
    if (currentMonth) topCells.push({ label: currentMonth, width: monthWidth });
  } else if (scaleView === "week") {
    let cursor = start.startOf("week");
    let currentMonth = "";
    let monthWidth = 0;
    while (cursor.isBefore(end)) {
      const weekEnd = cursor.add(6, "day");
      const weekWidth = 7 * pxPerDay;
      const monthLabel = cursor.format("MMMM YYYY");
      if (monthLabel !== currentMonth) {
        if (currentMonth) topCells.push({ label: currentMonth, width: monthWidth });
        currentMonth = monthLabel;
        monthWidth = 0;
      }
      monthWidth += weekWidth;
      bottomCells.push({
        label: `${cursor.format("M/D")}\u2013${weekEnd.format("M/D")}`,
        width: weekWidth,
        isWeekend: false,
      });
      cursor = cursor.add(1, "week");
    }
    if (currentMonth) topCells.push({ label: currentMonth, width: monthWidth });
  } else {
    let cursor = start.startOf("month");
    let currentYear = "";
    let yearWidth = 0;
    while (cursor.isBefore(end)) {
      const daysInMonth = cursor.daysInMonth();
      const monthWidth = daysInMonth * pxPerDay;
      const yearLabel = cursor.format("YYYY");
      if (yearLabel !== currentYear) {
        if (currentYear) topCells.push({ label: currentYear, width: yearWidth });
        currentYear = yearLabel;
        yearWidth = 0;
      }
      yearWidth += monthWidth;
      bottomCells.push({
        label: cursor.format("MMM"),
        width: monthWidth,
        isWeekend: false,
      });
      cursor = cursor.add(1, "month");
    }
    if (currentYear) topCells.push({ label: currentYear, width: yearWidth });
  }

  return { topCells, bottomCells };
}

export default function JobTimeline({
  jobs,
  scaleView,
  onZoomChange,
  zoomRef,
  centerKey,
  focusMs,
  nowMs: nowMsProp,
}: JobTimelineProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false,
    x: 0,
    y: 0,
    task: null,
  });
  const [zoomOverride, setZoomOverride] = useState<number | null>(null);
  const [dragStart, setDragStart] = useState<{ x: number; scrollX: number } | null>(null);
  const [dragEnd, setDragEnd] = useState<number | null>(null);

  // Expose reset and zoom state to parent
  const resetZoom = useCallback(() => setZoomOverride(null), []);
  const geometryRef = useRef({ startMs: 0, pxPerDay: 1 });
  const scrollToDate = useCallback((ms: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const { startMs, pxPerDay: ppd } = geometryRef.current;
    const x = ((ms - startMs) / 86400000) * ppd;
    const viewportW = el.clientWidth - LABEL_W;
    el.scrollLeft = Math.max(0, x - viewportW / 2);
  }, []);
  const scrollToToday = useCallback(() => scrollToDate(Date.now()), [scrollToDate]);
  if (zoomRef) zoomRef.current = { reset: resetZoom, scrollToToday, scrollToDate };
  const prevZoomed = useRef(false);
  if (onZoomChange && (zoomOverride !== null) !== prevZoomed.current) {
    prevZoomed.current = zoomOverride !== null;
    onZoomChange(zoomOverride !== null);
  }

  const { lanes, timelineStartMs, totalWidth, topCells, bottomCells, todayX } =
    useMemo(() => {
      if (!jobs?.length)
        return {
          lanes: [],
          timelineStartMs: 0,
          totalWidth: 0,
          topCells: [],
          bottomCells: [],
          todayX: 0,
        };

      const pxPerDay = zoomOverride ?? PX_PER_DAY[scaleView];

      let globalMin = Infinity;
      let globalMax = -Infinity;

      const jobLanes = (jobs as any[])
        .map((job: any, jobIndex: number) => {
          const scheduled = (job.job_tasks || [])
            .filter((jt: any) => jt.start_date && jt.status !== "completed")
            .sort(
              (a: any, b: any) =>
                new Date(a.start_date).getTime() -
                new Date(b.start_date).getTime()
            );

          if (scheduled.length === 0) return null;

          const color = PALETTE[jobIndex % PALETTE.length];
          const tasks = scheduled.map((jt: any) => {
            const isSlotOnly =
              (jt.task_staff_capacity_method === "slot" &&
                jt.task_machine_method === "none") ||
              !jt.end_date ||
              jt.start_date === jt.end_date;

            const startMs = new Date(jt.start_date).getTime();
            const endMs = isSlotOnly
              ? startMs
              : new Date(jt.end_date).getTime();

            if (startMs < globalMin) globalMin = startMs;
            const taskMax = isSlotOnly ? startMs : endMs;
            if (taskMax > globalMax) globalMax = taskMax;

            const displayName =
              jt.batch_index > 0
                ? `${jt.task_name} (Batch ${jt.batch_index})`
                : jt.task_name;

            return {
              id: jt.id,
              name: displayName,
              taskCode: jt.task_task_id,
              startMs,
              endMs,
              isSlotOnly,
              staffName: jt.assigned_staff_name,
              machineName: jt.assigned_machine_name,
              latestStart: jt.latest_start_date,
              progress: Number(jt.percent_complete) || 0,
              color,
            };
          });

          const { packed, subRowCount } = packIntoSubRows(tasks);
          const laneHeight =
            subRowCount * (BAR_H + BAR_GAP) + LANE_PAD * 2;

          return {
            jobId: job.id,
            jobName: `${job.order_name} \u2013 ${job.part_name}`,
            dueDate: job.adjusted_required_completion_date,
            color,
            tasks: packed,
            subRowCount,
            laneHeight,
          };
        })
        .filter(Boolean);

      // Keep room on both sides of now so it can be centered even when every
      // task is in the past or the future.
      const nowMs = nowMsProp ?? Date.now();
      const nowPadMs = NOW_PAD_DAYS[scaleView] * 86400000;
      globalMin = Math.min(globalMin, nowMs - nowPadMs);
      globalMax = Math.max(globalMax, nowMs + nowPadMs);

      const padDays = scaleView === "day" ? 1 : scaleView === "week" ? 3 : 7;
      const tlStart = dayjs(globalMin).subtract(padDays, "day").startOf("day");
      const tlEnd = dayjs(globalMax).add(padDays, "day").endOf("day");
      const totalDays = tlEnd.diff(tlStart, "day") + 1;
      const totalW = totalDays * pxPerDay;

      const { topCells: tc, bottomCells: bc } = buildDateCells(
        tlStart,
        tlEnd,
        pxPerDay,
        scaleView
      );

      const nowX = ((nowMs - tlStart.valueOf()) / 86400000) * pxPerDay;

      return {
        lanes: jobLanes,
        timelineStartMs: tlStart.valueOf(),
        totalWidth: totalW,
        topCells: tc,
        bottomCells: bc,
        todayX: nowX,
      };
    }, [jobs, scaleView, zoomOverride, nowMsProp]);

  const pxPerDay = zoomOverride ?? PX_PER_DAY[scaleView];
  geometryRef.current = { startMs: timelineStartMs, pxPerDay };
  const focusRef = useRef(focusMs);
  focusRef.current = focusMs;
  const hasLanes = lanes.length > 0;

  // Center on the focus date when the chart first renders and when the scale
  // or filters change. Drag-zoom and data refetches keep the user's position.
  useLayoutEffect(() => {
    if (hasLanes) scrollToDate(focusRef.current ?? Date.now());
  }, [hasLanes, scaleView, centerKey, scrollToDate]);

  const toX = useCallback(
    (ms: number) => ((ms - timelineStartMs) / 86400000) * pxPerDay,
    [timelineStartMs, pxPerDay]
  );

  const handleBarEnter = useCallback(
    (e: React.MouseEvent, task: any) => {
      setTooltip({
        visible: true,
        x: e.clientX + 12,
        y: e.clientY - 10,
        task,
      });
    },
    []
  );

  const handleBarMove = useCallback(
    (e: React.MouseEvent) => {
      setTooltip((prev) => ({
        ...prev,
        x: e.clientX + 12,
        y: e.clientY - 10,
      }));
    },
    []
  );

  const handleBarLeave = useCallback(() => {
    setTooltip((prev) => ({ ...prev, visible: false }));
  }, []);

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    // Only start drag on background clicks (not on bars)
    if ((e.target as HTMLElement).closest("[data-bar]")) return;
    const el = scrollRef.current;
    if (!el) return;
    setDragStart({ x: e.clientX, scrollX: el.scrollLeft });
    setDragEnd(null);
  }, []);

  const handleDragMove = useCallback((e: React.MouseEvent) => {
    if (!dragStart) return;
    setDragEnd(e.clientX);
  }, [dragStart]);

  const handleDragEnd = useCallback(() => {
    if (!dragStart || dragEnd === null) {
      setDragStart(null);
      setDragEnd(null);
      return;
    }
    const el = scrollRef.current;
    if (!el) { setDragStart(null); setDragEnd(null); return; }

    const rect = el.getBoundingClientRect();
    const labelW = 220; // LABEL_W
    const startX = Math.min(dragStart.x, dragEnd) - rect.left - labelW + dragStart.scrollX;
    const endX = Math.max(dragStart.x, dragEnd) - rect.left - labelW + el.scrollLeft;
    const dragPixelWidth = Math.abs(endX - startX);

    if (dragPixelWidth < 20) {
      // Too small — ignore
      setDragStart(null);
      setDragEnd(null);
      return;
    }

    // Calculate how many days the selection spans
    const daysSelected = dragPixelWidth / pxPerDay;
    // New pxPerDay to fit that range in the visible area
    const viewportWidth = rect.width - labelW;
    const newPxPerDay = Math.max(4, Math.min(2400, viewportWidth / daysSelected));

    setZoomOverride(newPxPerDay);

    // Scroll to the start of the selection
    const scrollToX = (startX / pxPerDay) * newPxPerDay;
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollLeft = scrollToX;
    });

    setDragStart(null);
    setDragEnd(null);
  }, [dragStart, dragEnd, pxPerDay]);

  const fmt = (v: any) =>
    v ? formatDateTime(v, "MM/DD/YYYY HH:mm") : "\u2014";

  if (!lanes.length) return null;

  const headerH = 48;

  return (
    <div
      ref={scrollRef}
      onMouseDown={handleDragStart}
      onMouseMove={handleDragMove}
      onMouseUp={handleDragEnd}
      onMouseLeave={() => { setDragStart(null); setDragEnd(null); }}
      style={{
        position: "relative",
        height: "calc(100vh - 220px)",
        overflow: "auto",
        border: "1px solid #e8e8e8",
        borderRadius: 6,
        background: "#fff",
        cursor: dragStart ? "col-resize" : "default",
      }}
    >
      {/* ── HEADER ── */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          display: "flex",
          background: "#fafafa",
          borderBottom: "1px solid #d9d9d9",
          minWidth: LABEL_W + totalWidth,
        }}
      >
        <div
          style={{
            width: LABEL_W,
            minWidth: LABEL_W,
            position: "sticky",
            left: 0,
            zIndex: 11,
            background: "#fafafa",
            borderRight: "1px solid #d9d9d9",
            display: "flex",
            alignItems: "center",
            padding: "0 10px",
            fontWeight: 600,
            fontSize: 12,
          }}
        >
          Job
        </div>
        <div style={{ flex: 1 }}>
          {/* top row */}
          <div style={{ display: "flex", borderBottom: "1px solid #e8e8e8" }}>
            {topCells.map((c, i) => (
              <div
                key={i}
                style={{
                  width: c.width,
                  textAlign: "center",
                  fontSize: 11,
                  fontWeight: 600,
                  padding: "3px 0",
                  borderRight: "1px solid #e8e8e8",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                }}
              >
                {c.label}
              </div>
            ))}
          </div>
          {/* bottom row */}
          <div style={{ display: "flex" }}>
            {bottomCells.map((c, i) => (
              <div
                key={i}
                style={{
                  width: c.width,
                  textAlign: "center",
                  fontSize: 10,
                  padding: "2px 0",
                  borderRight: "1px solid #f0f0f0",
                  background: c.isWeekend ? "#f5f5f5" : "transparent",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                }}
              >
                {c.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── BODY ── */}
      <div style={{ minWidth: LABEL_W + totalWidth }}>
        {lanes.map((lane: any) => (
          <div
            key={lane.jobId}
            style={{
              display: "flex",
              borderBottom: "1px solid #f0f0f0",
              minHeight: lane.laneHeight,
            }}
          >
            {/* label */}
            <div
              style={{
                width: LABEL_W,
                minWidth: LABEL_W,
                position: "sticky",
                left: 0,
                zIndex: 5,
                background: "#fff",
                borderRight: "1px solid #d9d9d9",
                padding: "4px 8px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                fontSize: 12,
              }}
            >
              <div
                style={{
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={lane.jobName}
              >
                <span
                  style={{
                    display: "inline-block",
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: lane.color,
                    marginRight: 6,
                    flexShrink: 0,
                  }}
                />
                {lane.jobName}
              </div>
              {lane.dueDate && (
                <div style={{ fontSize: 10, color: "#888", marginTop: 1 }}>
                  Due {formatDate(lane.dueDate)}
                </div>
              )}
            </div>

            {/* timeline lane */}
            <div
              style={{
                flex: 1,
                position: "relative",
                height: lane.laneHeight,
              }}
            >
              {/* weekend stripes for day view */}
              {scaleView === "day" &&
                bottomCells.map((c: any, i: number) =>
                  c.isWeekend ? (
                    <div
                      key={`wk-${i}`}
                      style={{
                        position: "absolute",
                        left: bottomCells
                          .slice(0, i)
                          .reduce((s: number, cc: any) => s + cc.width, 0),
                        top: 0,
                        width: c.width,
                        height: "100%",
                        background: "rgba(0,0,0,0.02)",
                        pointerEvents: "none",
                      }}
                    />
                  ) : null
                )}

              {/* today marker */}
              {todayX > 0 && todayX < totalWidth && (
                <div
                  style={{
                    position: "absolute",
                    left: todayX,
                    top: 0,
                    width: 2,
                    height: "100%",
                    background: "#f5222d",
                    zIndex: 3,
                    pointerEvents: "none",
                  }}
                />
              )}

              {/* task bars */}
              {lane.tasks.map((task: any) => {
                const x = toX(task.startMs);
                const barTop =
                  LANE_PAD + task.subRow * (BAR_H + BAR_GAP);

                if (task.isSlotOnly) {
                  const cx = x;
                  return (
                    <div
                      key={task.id}
                      onMouseEnter={(e) => handleBarEnter(e, task)}
                      onMouseMove={handleBarMove}
                      onMouseLeave={handleBarLeave}
                      title={task.name}
                      style={{
                        position: "absolute",
                        left: cx - MILESTONE_SIZE / 2,
                        top: barTop + (BAR_H - MILESTONE_SIZE) / 2,
                        width: MILESTONE_SIZE,
                        height: MILESTONE_SIZE,
                        background: darken(task.color),
                        transform: "rotate(45deg)",
                        borderRadius: 1,
                        zIndex: 4,
                        cursor: "pointer",
                      }}
                    />
                  );
                }

                const w = Math.max(
                  MIN_BAR_W,
                  toX(task.endMs) - x
                );

                return (
                  <div
                    key={task.id}
                    onMouseEnter={(e) => handleBarEnter(e, task)}
                    onMouseMove={handleBarMove}
                    onMouseLeave={handleBarLeave}
                    style={{
                      position: "absolute",
                      left: x,
                      top: barTop,
                      width: w,
                      height: BAR_H,
                      background: lighten(task.color, 0.5),
                      border: `1px solid ${task.color}`,
                      borderRadius: 3,
                      overflow: "hidden",
                      cursor: "pointer",
                      zIndex: 4,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {/* progress fill */}
                    {task.progress > 0 && (
                      <div
                        style={{
                          position: "absolute",
                          left: 0,
                          top: 0,
                          height: "100%",
                          width: `${Math.min(100, task.progress)}%`,
                          background: task.color,
                          opacity: 0.3,
                        }}
                      />
                    )}
                    {w > 30 && (
                      <span
                        style={{
                          position: "relative",
                          zIndex: 1,
                          fontSize: 10,
                          fontWeight: 500,
                          color: "#333",
                          padding: "0 4px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: w - 8,
                        }}
                      >
                        {task.name}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* ── DRAG SELECTION OVERLAY ── */}
      {dragStart && dragEnd !== null && (
        <div
          style={{
            position: "fixed",
            left: Math.min(dragStart.x, dragEnd),
            top: scrollRef.current?.getBoundingClientRect().top ?? 0,
            width: Math.abs(dragEnd - dragStart.x),
            height: scrollRef.current?.getBoundingClientRect().height ?? 0,
            background: "rgba(22, 119, 255, 0.1)",
            border: "1px dashed #1677ff",
            pointerEvents: "none",
            zIndex: 50,
          }}
        />
      )}

      {/* Reset zoom button is rendered outside via onResetZoom prop */}

      {/* ── TOOLTIP ── */}
      {tooltip.visible && tooltip.task && (
        <div
          style={{
            position: "fixed",
            left: tooltip.x,
            top: tooltip.y,
            zIndex: 9999,
            background: "#fff",
            border: "1px solid #d9d9d9",
            borderRadius: 6,
            padding: "8px 12px",
            fontSize: 12,
            lineHeight: 1.7,
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
            pointerEvents: "none",
            whiteSpace: "nowrap",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 2 }}>
            {tooltip.task.taskCode && `[${tooltip.task.taskCode}] `}
            {tooltip.task.name}
          </div>
          <div>Start: {fmt(tooltip.task.startMs)}</div>
          {!tooltip.task.isSlotOnly && (
            <div>End: {fmt(tooltip.task.endMs)}</div>
          )}
          {tooltip.task.staffName && (
            <div>Staff: {tooltip.task.staffName}</div>
          )}
          {tooltip.task.machineName && (
            <div>Machine: {tooltip.task.machineName}</div>
          )}
          {tooltip.task.latestStart && (
            <div>Latest Start: {fmt(tooltip.task.latestStart)}</div>
          )}
        </div>
      )}
    </div>
  );
}
